import React, { useState, useEffect } from 'react';
import {
  faEdit,
  faSlidersV,
  faSave,
  faTrash,
  faBan,
  faLightbulbSlash,
  faPhoneSquareAlt,
  faPhone,
  faEnvelope
} from '@fortawesome/pro-solid-svg-icons';
import { FormInstance, Form } from '@lightbeamhealth/blueprint-form';
import moment from 'moment-timezone';

import { Row, Col } from '../../../grid';
import Icon from '../../atoms/icon';
import { Tabs, Tab } from '../../molecules/tab';
import Button from '../../molecules/button';
import Card, { CardFrame, CardBlock } from '../../organisms/card';
import Modal from '../../organisms/modal';
import Toast from '../../organisms/toast';
import Callout from '../../organisms/callout';
import SelectContact from '../../organisms/contact/select';
import { ScheduleFormProvider } from '../../../contexts/form';
import { AppKey } from '../../../libs/apps';
import AppConfigs from '../../../libs/apps/configs';
import { AppConfig } from '../../../libs/apps';
import { ISchedule } from '../../../libs/schedule';
import { FormatDate, TIME_FORMATS } from '../../../libs/time';
import { DeserializeNumbers, FormatPhone } from '../../../libs/format';
import { PostJSON, BaseResponse } from '../../../libs/request';
import { useGetSettings } from '../../../libs/settings';
import { RouteMapping } from '../../../libs/route';
import { useTriageUpdater } from '../../../contexts/triage/updater';
import { useTriagePatient } from '../../../contexts/triage/patient';
import { useMetaData } from '../../../contexts/metadata';
import { ICustomProgram } from '../../../libs/composer';

interface ScheduleResponse extends BaseResponse {
  schedule?: any;
}

interface SelectContactData {
  contactId?: number;
}

interface SelectContactFormProps {
  form: FormInstance<SelectContactData>;
  onChange?: (data: SelectContactData) => any;
  isDisabled?: boolean;
}

const SelectContactForm: React.FC<SelectContactFormProps> = ({
  form,
  onChange,
  isDisabled
}) => {
  return (
    <Form form={form} onChange={onChange} disabled={isDisabled}>
      <SelectContact label="Associated Contact" name="contactId" fill />
    </Form>
  );
};

interface ProgramPrescriptionProps {
  appKey: AppKey;
  schedule?: ISchedule;
  routes?: RouteMapping;
  customProgram?: ICustomProgram;
}

const IsActiveSchedule = (schedule: ISchedule) => {
  if (!schedule) {
    return false;
  }
  if (schedule.end && schedule.end <= new Date()) {
    return false;
  }
  return !schedule.isCanceled;
};

/**
 * Process the schedule for form use
 *
 * @param schedule the raw schedule returned from the backend
 * @param config the program config
 */
const ProcessSchedule = (schedule: any, config: AppConfig): any => {
  let data: any = {
    ...schedule
  };
  if (!config.shouldSkipDefaultProcess) {
    // When days are not properly parsed
    if (schedule.days != null && !Array.isArray(schedule.days)) {
      data.days = DeserializeNumbers(schedule.days);
    }
    // When times are not properly parsed
    if (schedule.times != null && !Array.isArray(schedule.times)) {
      if (schedule.times === '') {
        data.times = [];
      } else {
        data.times = schedule.times.split(',');
      }
    }
    // Turn the start & end dates to proper formats
    const start = moment.tz(schedule.start, schedule.tz);
    data.start = start.format('YYYY-MM-DD');
    if (schedule.end) {
      const end = moment.tz(schedule.end, schedule.tz);
      data.end = end.format('YYYY-MM-DD');
    } else {
      data.end = null;
    }
  }
  if (config.processSchedule) {
    data = config.processSchedule(data, schedule);
  }
  return data;
};

const ProgramPrescription: React.FC<ProgramPrescriptionProps> = ({
  appKey,
  schedule,
  routes,
  customProgram
}): JSX.Element => {
  const { terms } = useMetaData();
  const { isReadOnly } = useMetaData();
  const {
    patientData: { patient }
  } = useTriagePatient();
  const { handleUpdateSchedule } = useTriageUpdater();
  const [rxForm, setRXForm] = useState<FormInstance<any>>(null);
  const [settingsForm, setSettingsForm] = useState<FormInstance<any>>(null);
  const [contactId, setContactId] = useState<number>(
    schedule?.contactId || null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const contactForm = new FormInstance<SelectContactData>({
    contactId: schedule ? schedule.contactId : null
  });
  const config = customProgram ? AppConfigs['generic'] : AppConfigs[appKey];
  const endpointKey = customProgram ? 'composer' : appKey;
  // Set up the handlers
  const updateSchedule = (data: any) => {
    const params = {
      ...data,
      contact: contactId,
      id: schedule.id
    };
    if (customProgram) {
      params.program = customProgram.id;
    }
    setIsLoading(true);
    PostJSON<ScheduleResponse>({
      url: `/${endpointKey}/edit/`,
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Updating Settings',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The settings have been updated successfully!'
      });
      handleUpdateSchedule(
        patient,
        appKey,
        (response as ScheduleResponse).schedule
      );
    });
  };
  const cancelSchedule = () => {
    const handleCancelConfirm = () => {
      setIsLoading(true);
      PostJSON<ScheduleResponse>({
        url: `/${endpointKey}/cancel/`,
        params: {
          id: schedule.id,
          program: customProgram ? customProgram.id : null
        }
      }).then((response) => {
        setIsLoading(false);
        if (response.status !== 'OK') {
          Modal.error({
            title: 'Error Removing Prescription',
            content: response.message
          });
          return;
        }
        Modal.success({
          title: 'Prescription Removed',
          content: `The prescription for the program has been removed successfully. You may restart it by enrolling the ${terms.patient.default} again.`
        });
        handleUpdateSchedule(
          patient,
          appKey,
          (response as ScheduleResponse).schedule
        );
      });
    };

    setIsLoading(true);
    PostJSON<{ status: string; messages: string[] }>({
      url: '/sunset/preview/',
      params: {
        app: appKey,
        patientId: patient.id
      }
    })
      .then((previewResponse) => {
        setIsLoading(false);
        const messages =
          previewResponse.status === 'OK' && 'messages' in previewResponse
            ? previewResponse.messages
            : [];
        Modal.confirm({
          title: 'Remove Prescription',
          content: (
            <div>
              <p>
                Are you sure you want to remove the current active prescription
                of the program for this {terms.patient.default}?
              </p>
              {messages && messages.length > 0 && (
                <div className="mt-4">
                  <strong>The following sunset message(s) will be sent:</strong>
                  {messages.map((msg, i) => (
                    <Callout key={i} className="mt-2">
                      {msg}
                    </Callout>
                  ))}
                </div>
              )}
            </div>
          ),
          onOk: handleCancelConfirm
        });
      })
      .catch(() => {
        setIsLoading(false);
        Modal.confirm({
          title: 'Remove Prescription',
          content: `Are you sure you want to remove the current active prescription of the program for this ${terms.patient.default}?`,
          onOk: handleCancelConfirm
        });
      });
  };
  const updateThresholds = (data: any) => {
    const params = {
      app: endpointKey,
      patient: patient.id,
      settings: data
    };
    setIsLoading(true);
    PostJSON<ScheduleResponse>({
      url: '/settings/edit/',
      params
    }).then((response) => {
      setIsLoading(false);
      if (response.status !== 'OK') {
        Modal.error({
          title: 'Error Updating Thresholds',
          content: response.message
        });
        return;
      }
      Toast({
        intent: 'success',
        message: 'The thresholds have been updated successfully!'
      });
    });
  };
  // Set up the forms
  const rxFormNode = config?.form
    ? React.createElement(config.form, {
        setForm: setRXForm,
        onSubmit: updateSchedule,
        rawSchedule: schedule,
        isDisabled: isReadOnly,
        customProgram: customProgram
      })
    : null;
  const settingsFormNode = config?.settings
    ? React.createElement(config.settings, {
        setForm: setSettingsForm,
        onSubmit: updateThresholds,
        disabled: isReadOnly,
        customProgram: customProgram
      })
    : null;
  // Fetch the settings if needed
  const { settings } = useGetSettings({
    appKey: endpointKey,
    patientId: patient.id,
    contactId: schedule?.contactId,
    isEnabled: config?.settings != null,
    programId: customProgram ? customProgram.id : null
  });
  // Set the schedule data to the form
  useEffect(() => {
    if (rxForm && schedule && !schedule.isCanceled) {
      rxForm.setData(ProcessSchedule(schedule, config));
    }
  }, [schedule, rxForm]);
  useEffect(() => {
    if (settingsForm && settings) {
      const values = JSON.parse(settings.values);
      settingsForm.setData(values);
    }
  }, [settings, settingsForm]);
  return (
    <Tabs className="mb-4" id="Prescription" vertical>
      {config == null ? (
        <Tab
          id="Schedule"
          title={
            <span>
              <Icon icon={faEdit} className="mr-4" /> Settings
            </span>
          }
          panel={
            <Card>
              <h4>
                <Icon icon={faLightbulbSlash} className="mr-2" /> Program
                Deprecated
              </h4>
              <p>
                This program has reached End of Life and is no longer supported
                by CareSignal. Please contact support if you have any questions.
              </p>
            </Card>
          }
        />
      ) : config.form && IsActiveSchedule(schedule) ? (
        <Tab
          id="Schedule"
          title={
            <span>
              <Icon icon={faEdit} className="mr-4" /> Settings
            </span>
          }
          panel={
            <>
              <CardFrame>
                <ScheduleFormProvider
                  value={{
                    isEditMode: true
                  }}
                >
                  {rxFormNode}
                </ScheduleFormProvider>
                <CardBlock>
                  <Row>
                    <Col sm={12}>
                      <SelectContactForm
                        form={contactForm}
                        onChange={({ contactId }) => setContactId(contactId)}
                        isDisabled={isReadOnly}
                      />
                    </Col>
                  </Row>
                </CardBlock>
                {!isReadOnly && (
                  <CardBlock>
                    <Button
                      intent="success"
                      loading={isLoading}
                      onClick={() => rxForm.submit()}
                    >
                      <Icon icon={faSave} className="mr-2" /> Update Settings
                    </Button>
                    <Button
                      className="ml-4"
                      intent="danger"
                      loading={isLoading}
                      onClick={cancelSchedule}
                    >
                      <Icon icon={faTrash} className="mr-2" /> Remove
                      Prescription
                    </Button>
                  </CardBlock>
                )}
              </CardFrame>
            </>
          }
        />
      ) : (
        <Tab
          id="Schedule"
          title={
            <span>
              <Icon icon={faEdit} className="mr-4" /> Settings
            </span>
          }
          panel={
            <Card>
              <h4>
                <Icon icon={faBan} className="mr-2" /> No Active Prescription
              </h4>
              <p>
                {schedule && schedule.isCanceled
                  ? `The prescription for this program was canceled on ${FormatDate(
                      schedule.canceledAt,
                      TIME_FORMATS.DATE,
                      schedule.tz
                    )}`
                  : schedule && schedule.end
                    ? `The prescription for this program has ended on ${FormatDate(
                        schedule.end,
                        TIME_FORMATS.DATE,
                        schedule.tz
                      )}.`
                    : 'The prescription for this program has ended or been canceled.'}
              </p>
            </Card>
          }
        />
      )}
      {config && config.settings ? (
        <Tab
          id="Settings"
          title={
            <span>
              <Icon icon={faSlidersV} className="mr-4" /> Thresholds
            </span>
          }
          panel={
            <>
              <CardFrame>
                {settingsFormNode}
                {!isReadOnly && (
                  <CardBlock>
                    <Button
                      intent="success"
                      icon={<Icon icon={faSave} className="text-white" />}
                      loading={isLoading}
                      onClick={() => settingsForm.submit()}
                    >
                      Update Thresholds
                    </Button>
                  </CardBlock>
                )}
              </CardFrame>
            </>
          }
        />
      ) : null}
      {routes ? (
        <Tab
          id="Routes"
          title={
            <span>
              <Icon icon={faPhoneSquareAlt} className="mr-4" /> Phones
            </span>
          }
          panel={
            <Card>
              <Callout>
                The following are phone numbers used by CareSignal to
                communicate with the {terms.patient.default} for this program.
              </Callout>
              <Card className="mt-4" interactive>
                <Icon icon={faPhone} /> Automated Calls:{' '}
                <b>{FormatPhone(routes.call, true)}</b>
              </Card>
              <Card className="mt-4" interactive>
                <Icon icon={faEnvelope} /> Text Messages:{' '}
                <b>{FormatPhone(routes.message, true)}</b>
              </Card>
              <Callout className="mt-4" intent="primary">
                You may ask the {terms.patient.default} to save the phone
                numbers in their contacts to reduce the chance of the
                calls/messages being mistaken as SPAM.
              </Callout>
            </Card>
          }
        />
      ) : null}
    </Tabs>
  );
};

export default ProgramPrescription;
